import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIeKitLabels, useIeKitIdPrefix } from '@/hooks/useIeKitLabels';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Straighten as RulerIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Entity } from '@/types/Entity';
import { useEntities } from '@/hooks';
import { entityService } from '@/services/EntityService';
import { getErrorMessage } from '@/utils/httpClient';
import { iemService, type IEItem } from '@/services/iem';
import { useNotifications } from '@/stores/useNotificationStore';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useAuthStore } from '@/stores/useAuthStore';
import { getActorIdFromToken } from '@/utils/jwtUtils';
import { useBulkAllocateToCart } from '@/hooks/mutations/cart/useCartMutations';
import { cartService } from '@/services/cart';
import { cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import { buildReserveStockPlan } from '@/components/iem/shared/reserveStockPlanner';

import {
  DualPaneSelectionDialog,
  SelectionItem,
} from '@/components/shared/DualPaneSelectionDialog';
import { PersonSizingDialog } from '../dialogs/PersonSizingDialog';

const BULK_SAVE_LIMIT = 50;

interface KitAssignmentDialogProps {
  open: boolean;
  kit: Entity | null;
  onClose: () => void;
  onSaved?: (result: { added: number; removed: number }) => void;
}

// --- Types ---

interface PersonEntityData {
  rank?: string;
  unit?: string;
  team?: string;
}

interface AssignmentEntityData {
  person_id?: string;
  ie_kit_id?: string;
}

interface KitItemEntityData {
  catalog_id?: string;
}

interface SizingEntityData {
  person_id?: string;
  parent_id?: string;
  catalog_id?: string;
}

interface CatalogEntityData {
  parent_id?: string;
  size?: string;
  size_label?: string;
  lin?: string;
  nsn?: string;
}

interface KitItemWithQuantity {
  catalog_id?: string;
  ie_kit_id?: string;
  quantity?: number;
}

interface AssetReservationEntityData {
  customer_id?: string;
  catalog_id?: string;
  lin?: string;
  nsn?: string;
  ie_kit_id?: string;
}

// --- Helpers ---

const normalizePersonText = (entity: Entity): string => {
  const fields: (string | undefined)[] = [entity.display_name, entity.entity_identifier];
  const data = (entity.entity_data ?? {}) as PersonEntityData;
  if (data.rank) fields.push(data.rank);
  if (data.unit) fields.push(data.unit);
  if (data.team) fields.push(data.team);
  return fields.filter(Boolean).join(' ').trim().toLowerCase();
};

const getPersonSubtitle = (entity: Entity, individualIdPrefix: string): string => {
  const data = (entity.entity_data ?? {}) as PersonEntityData;
  const displayId = entity.entity_identifier?.replace(/^INDV-/, individualIdPrefix);
  return [displayId, data.rank, data.unit].filter(Boolean).join(' • ');
};

const buildAssignmentIdentifier = (person: Entity, kit: Entity): string => {
  const sanitize = (s?: string) => (s || 'X').toUpperCase().replace(/[^A-Z0-9]+/g, '-');
  const suffix = Math.random().toString(36).substring(2, 7).toUpperCase();
  return `PBK-${sanitize(person.entity_identifier).slice(0, 15)}-${sanitize(kit.entity_identifier).slice(0, 15)}-${suffix}`;
};

export const KitAssignmentDialog: React.FC<KitAssignmentDialogProps> = ({
  open,
  kit,
  onClose,
  onSaved,
}) => {
  const isViewer = useWriteGuard();
  // Resolve the authenticated user's actor UUID from the access token so it can
  // be passed explicitly to bulk entity creation. Without this, the DB function
  // falls back to JWT sub extraction at the PostgREST layer — which fails in
  // some auth configurations and produces: "Actor is required for bulk entity
  // creation" (#976).
  const accessToken = useAuthStore((s) => s.accessToken);
  const actorId = accessToken ? (getActorIdFromToken(accessToken) ?? undefined) : undefined;
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const { individualIdPrefix } = usePersonLabels('front_window');
  const { formatIdentifier: formatIeKitId } = useIeKitIdPrefix();
  const kitId = kit?.id ?? null;
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());
  const [loadedKitId, setLoadedKitId] = useState<string | null>(null);
  const [availableSearch, setAvailableSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingRemoveItem, setPendingRemoveItem] = useState<SelectionItem | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [hideExisting, setHideExisting] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState<Set<string>>(new Set());
  const [reservedCounts, setReservedCounts] = useState<
    Map<string, { current: number; authorized: number }>
  >(new Map());
  const [ieSummaryByPerson, setIeSummaryByPerson] = useState<Map<string, IEItem[]>>(new Map());
  const [reservedCartOverrides, setReservedCartOverrides] = useState<Set<string>>(new Set());
  const [sessionReservedByPerson, setSessionReservedByPerson] = useState<
    Map<string, Map<string, number>>
  >(new Map());
  const lastReservationByPersonRef = useRef<Map<string, number>>(new Map());
  const reservationInFlightRef = useRef<Set<string>>(new Set());

  // --- Reservation Data Fetching ---
  const assignedPersonIdsForReservations = useMemo(() => Array.from(draftIds), [draftIds]);

  const {
    data: dbReservations = [],
    isFetched: dbReservationsFetched,
    dataUpdatedAt: dbReservationsUpdatedAt,
  } = useEntities({
    type: 'asset_reservation',
    filters:
      assignedPersonIdsForReservations.length > 0 && kitId
        ? {
            'entity_data->>customer_id': `in.(${assignedPersonIdsForReservations.join(',')})`,
            or: `(entity_data->>ie_kit_id.eq.${kitId},entity_data->>ie_kit_id.is.null)`,
            status: 'eq.active',
          }
        : undefined,
    limit: 2000,
    enabled: open && !!kitId && assignedPersonIdsForReservations.length > 0,
  });

  // Sizing Dialog State
  const [sizingPerson, setSizingPerson] = useState<Entity | null>(null);

  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const bulkAllocateMutation = useBulkAllocateToCart();

  const { data: activeCarts = [] } = useQuery({
    queryKey: cartQueryKeys.activeAll(),
    queryFn: () => cartService.getAllActiveCarts(),
    enabled: open,
  });
  const activeIssueCartByPersonId = useMemo(() => {
    const map = new Map<string, (typeof activeCarts)[number]>();
    activeCarts.forEach((cart) => {
      if (!cart.customer_id) return;
      if (cart.workflow_type !== 'issue') return;
      map.set(cart.customer_id, cart);
    });
    return map;
  }, [activeCarts]);

  // --- Queries ---

  const {
    data: people = [],
    isLoading: peopleLoading,
    error: peopleError,
  } = useEntities({
    type: 'person',
    limit: 1000,
    order: 'display_name.asc',
    filters: { status: 'eq.active' },
    enabled: open,
  });

  const {
    data: assignments = [],
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useEntities({
    type: 'person_ie_kit',
    filters: kitId ? { 'entity_data->>ie_kit_id': `eq.${kitId}` } : undefined,
    limit: 500,
    enabled: open && !!kitId,
  });

  // --- Sizing Data Fetching ---

  // 1. Fetch Kit Items to find what needs sizing
  const { data: kitItems = [] } = useEntities({
    type: 'ie_kit_item',
    filters: kitId ? { 'entity_data->>ie_kit_id': `eq.${kitId}` } : undefined,
    limit: 500,
    enabled: open && !!kitId,
  });

  const kitCatalogIds = useMemo(() => {
    return kitItems
      .map((i) => (i.entity_data as KitItemEntityData).catalog_id as string)
      .filter(Boolean);
  }, [kitItems]);

  const kitItemCatalogIdById = useMemo(() => {
    const map = new Map<string, string>();
    kitItems.forEach((item) => {
      const catalogId = (item.entity_data as KitItemEntityData).catalog_id as string | undefined;
      if (catalogId) {
        map.set(item.id, catalogId);
      }
    });
    return map;
  }, [kitItems]);

  // 2. Fetch child items (variants) to see which kit items are "Parents" (sizeable)
  // Note: We assume if a catalog item has children, it is "sizeable".
  const { data: variants = [] } = useEntities({
    type: 'catalog',
    filters:
      kitCatalogIds.length > 0
        ? { 'entity_data->>parent_id': `in.(${kitCatalogIds.join(',')})` }
        : undefined,
    limit: 1000,
    enabled: open && kitCatalogIds.length > 0,
  });

  // 2b. Fetch catalog entities for kit items to support synthetic IE Kit items
  const { data: kitCatalogEntities = [] } = useEntities({
    type: 'catalog',
    filters: kitCatalogIds.length > 0 ? { id: `in.(${kitCatalogIds.join(',')})` } : undefined,
    limit: 500,
    enabled: open && kitCatalogIds.length > 0,
  });

  // Lookup map for catalog entities by ID
  const catalogEntityById = useMemo(() => {
    const map = new Map<string, Entity>();
    kitCatalogEntities.forEach((catalog) => {
      map.set(catalog.id, catalog);
    });
    return map;
  }, [kitCatalogEntities]);

  // Build synthetic IE Kit items from kit items for newly added (unsaved) people
  const syntheticIeItemsForKit = useMemo((): IEItem[] => {
    if (!kitId || kitItems.length === 0 || catalogEntityById.size === 0) {
      return [];
    }

    return kitItems.map((kitItem) => {
      const kitItemData = kitItem.entity_data as KitItemWithQuantity;
      const catalogId = kitItemData.catalog_id;
      const catalog = catalogId ? catalogEntityById.get(catalogId) : undefined;
      const catalogData = catalog?.entity_data as CatalogEntityData | undefined;

      return {
        item: formatNomenclatureWithSize(
          catalog?.display_name ?? 'Unknown Item',
          (catalogData?.size ?? null) as string | null,
        ),
        nsn: catalogData?.nsn ?? '',
        lin: catalogData?.lin ?? null,
        authorizedQty: kitItemData.quantity ?? 1,
        currentQty: 0,
        status: 'due',
        kitName: null,
        kitAssignmentName: null,
        optional: false,
        notes: null,
        personIeKitId: null,
        kitItemId: kitItem.id,
        ieKitId: kitId,
        catalogId: catalogId ?? null,
        catalogType: null,
        availableQty: 0, // Will be computed during reserve planning
      };
    });
  }, [kitId, kitItems, catalogEntityById]);

  const sizeableCatalogIds = useMemo(() => {
    const parents = new Set<string>();
    variants.forEach((v) => {
      const data = v.entity_data as CatalogEntityData;
      // Items with a parent_id are sized variants
      if (data.parent_id) {
        parents.add(data.parent_id);
      }
    });
    return parents;
  }, [variants]);

  const variantParentByCatalogId = useMemo(() => {
    const map = new Map<string, string>();
    variants.forEach((variant) => {
      const parentId = (variant.entity_data as CatalogEntityData).parent_id;
      if (!parentId) return;
      map.set(variant.id.toLowerCase(), parentId.toLowerCase());
    });
    return map;
  }, [variants]);

  const primaryKitIdByPerson = useMemo(() => {
    const map = new Map<string, string>();
    const normalizeLabel = (value?: string | null) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed || trimmed === '—') {
        return '~~~';
      }
      return trimmed;
    };

    ieSummaryByPerson.forEach((summary, personId) => {
      const kitLabels = new Map<string, string>();

      summary.forEach((item) => {
        const kitId = item.ieKitId?.trim();
        if (!kitId) return;
        const label = normalizeLabel(item.kitName ?? item.kitAssignmentName);
        if (!kitLabels.has(kitId)) {
          kitLabels.set(kitId, label);
        }
      });

      const sorted = Array.from(kitLabels.entries()).sort((a, b) => {
        const labelCompare = a[1].localeCompare(b[1]);
        if (labelCompare !== 0) return labelCompare;
        return a[0].localeCompare(b[0]);
      });

      if (sorted[0]) {
        map.set(personId, sorted[0][0]);
      }
    });

    return map;
  }, [ieSummaryByPerson]);

  const primaryKitIdByCatalogByPerson = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    const normalizeLabel = (value?: string | null) => {
      const trimmed = (value ?? '').trim();
      if (!trimmed || trimmed === '—') {
        return '~~~';
      }
      return trimmed;
    };

    ieSummaryByPerson.forEach((summary, personId) => {
      const catalogKits = new Map<string, Map<string, string>>();

      summary.forEach((item) => {
        const catalogId = item.catalogId?.trim().toLowerCase();
        const kitId = item.ieKitId?.trim();
        if (!catalogId || !kitId) return;

        const label = normalizeLabel(item.kitName ?? item.kitAssignmentName);
        const kitLabels = catalogKits.get(catalogId) ?? new Map<string, string>();
        if (!kitLabels.has(kitId)) {
          kitLabels.set(kitId, label);
        }
        catalogKits.set(catalogId, kitLabels);
      });

      const primaryByCatalog = new Map<string, string>();
      catalogKits.forEach((kitLabels, catalogId) => {
        const sorted = Array.from(kitLabels.entries()).sort((a, b) => {
          const labelCompare = a[1].localeCompare(b[1]);
          if (labelCompare !== 0) return labelCompare;
          return a[0].localeCompare(b[0]);
        });
        if (sorted[0]) {
          primaryByCatalog.set(catalogId, sorted[0][0]);
        }
      });

      map.set(personId, primaryByCatalog);
    });

    return map;
  }, [ieSummaryByPerson]);

  const shouldIncludeReservation = useCallback(
    (personId: string, data: AssetReservationEntityData) => {
      if (!kitId) return false;

      const reservationKitId = data.ie_kit_id?.trim();
      if (reservationKitId) {
        return reservationKitId === kitId;
      }

      const catalogId = data.catalog_id?.trim().toLowerCase();
      const primaryByCatalog = primaryKitIdByCatalogByPerson.get(personId);
      if (catalogId && primaryByCatalog?.has(catalogId)) {
        return primaryByCatalog.get(catalogId) === kitId;
      }

      const primaryKitId = primaryKitIdByPerson.get(personId);
      return primaryKitId === kitId;
    },
    [kitId, primaryKitIdByCatalogByPerson, primaryKitIdByPerson],
  );

  const dbReservedByPerson = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    const personReservationIds = new Map<string, Set<string>>();

    dbReservations.forEach((r) => {
      const data = r.entity_data as AssetReservationEntityData;
      const pid = data.customer_id;
      if (!pid || !shouldIncludeReservation(pid, data)) return;

      if (!map.has(pid)) map.set(pid, new Map());
      if (!personReservationIds.has(pid)) personReservationIds.set(pid, new Set());

      const personMap = map.get(pid)!;
      const seenIds = personReservationIds.get(pid)!;

      if (seenIds.has(r.id)) return;
      seenIds.add(r.id);

      if (data.catalog_id) {
        const catalogId = data.catalog_id.trim().toLowerCase();
        const k = `catalog:${catalogId}`;
        personMap.set(k, (personMap.get(k) ?? 0) + 1);

        const parentId = variantParentByCatalogId.get(catalogId);
        if (parentId) {
          const parentKey = `catalog:${parentId}`;
          if (parentKey !== k) {
            personMap.set(parentKey, (personMap.get(parentKey) ?? 0) + 1);
          }
        }
      }
      if (data.lin) {
        const k = `lin:${data.lin.trim().toLowerCase()}`;
        personMap.set(k, (personMap.get(k) ?? 0) + 1);
      }
      if (data.nsn) {
        const k = `nsn:${data.nsn.trim().toLowerCase()}`;
        personMap.set(k, (personMap.get(k) ?? 0) + 1);
      }
    });
    return map;
  }, [dbReservations, shouldIncludeReservation, variantParentByCatalogId]);

  useEffect(() => {
    if (!dbReservationsFetched) return;
    if (loadingReservations.size > 0) return;

    setSessionReservedByPerson((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);

      next.forEach((personMap, personId) => {
        const dbMap = dbReservedByPerson.get(personId);
        if (!dbMap) {
          const lastReservationAt = lastReservationByPersonRef.current.get(personId);
          if (lastReservationAt && dbReservationsUpdatedAt > lastReservationAt) {
            next.delete(personId);
            changed = true;
          }
          return;
        }

        const updated = new Map(personMap);
        personMap.forEach((count, key) => {
          const dbCount = dbMap.get(key) ?? 0;
          if (dbCount >= count) {
            updated.delete(key);
          }
        });

        if (updated.size === 0) {
          next.delete(personId);
          changed = true;
        } else if (updated.size !== personMap.size) {
          next.set(personId, updated);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [dbReservationsFetched, dbReservationsUpdatedAt, dbReservedByPerson, loadingReservations]);

  // 3. Fetch sizing records for assigned people
  const assignedPersonIds = useMemo(() => Array.from(draftIds), [draftIds]);

  const { data: peopleSizing = [] } = useEntities({
    type: 'person_sizing',
    filters:
      assignedPersonIds.length > 0
        ? { 'entity_data->>person_id': `in.(${assignedPersonIds.join(',')})` }
        : undefined,
    limit: 2000,
    enabled: open && assignedPersonIds.length > 0,
  });

  const personSizedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    // Map: PersonID -> Set<ParentCatalogID>
    const map = new Map<string, Set<string>>();

    peopleSizing.forEach((s) => {
      const data = s.entity_data as SizingEntityData;
      const pid = data.person_id;
      const parentId = data.parent_id;

      // Only count if this sized item corresponds to a sizeable item in the CURRENT kit
      if (pid && parentId && sizeableCatalogIds.has(parentId)) {
        if (!map.has(pid)) map.set(pid, new Set());
        map.get(pid)!.add(parentId);
      }
    });

    map.forEach((set, pid) => {
      counts.set(pid, set.size);
    });
    return counts;
  }, [peopleSizing, sizeableCatalogIds]);

  const sizingByPersonAndParent = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    peopleSizing.forEach((s) => {
      const data = s.entity_data as SizingEntityData;
      const personId = data.person_id;
      const parentId = data.parent_id;
      const catalogId = data.catalog_id;
      if (personId && parentId && catalogId) {
        if (!map.has(personId)) map.set(personId, new Map());
        map.get(personId)!.set(parentId, catalogId);
      }
    });
    return map;
  }, [peopleSizing]);

  const existingAssignmentsSet = useMemo(() => {
    const set = new Set<string>();
    assignments.forEach((a) => {
      const pid = (a.entity_data as AssignmentEntityData)?.person_id;
      if (pid) set.add(pid);
    });
    return set;
  }, [assignments]);

  const newAssignmentCount = useMemo(
    () => Array.from(draftIds).filter((id) => !existingAssignmentsSet.has(id)).length,
    [draftIds, existingAssignmentsSet],
  );
  const atLimit = newAssignmentCount >= BULK_SAVE_LIMIT;

  const isDirty = useMemo(() => {
    if (!open || loadedKitId !== kitId) return false;
    if (draftIds.size !== existingAssignmentsSet.size) return true;
    for (const id of draftIds) {
      if (!existingAssignmentsSet.has(id)) return true;
    }
    return false;
  }, [open, loadedKitId, kitId, draftIds, existingAssignmentsSet]);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const [sizeAvailabilityByCatalogId, setSizeAvailabilityByCatalogId] = useState<
    Map<string, number>
  >(new Map());

  useEffect(() => {
    if (!open || !kitId) {
      setSizeAvailabilityByCatalogId((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    const selections = new Map<string, string>();
    const personIds =
      draftIds.size > 0 ? Array.from(draftIds) : Array.from(ieSummaryByPerson.keys());

    personIds.forEach((personId) => {
      const sizingMap = sizingByPersonAndParent.get(personId);
      if (!sizingMap || sizingMap.size === 0) return;

      const isNewAssignment = !existingAssignmentsSet.has(personId);
      const summary = isNewAssignment ? syntheticIeItemsForKit : ieSummaryByPerson.get(personId);
      if (!summary || summary.length === 0) return;

      summary
        .filter((item) => item.ieKitId === kitId)
        .forEach((item) => {
          const catalogId = item.catalogId ?? undefined;
          const kitCatalogId = item.kitItemId ? kitItemCatalogIdById.get(item.kitItemId) : null;
          const sizingCatalogId = kitCatalogId ?? catalogId ?? null;
          if (!sizingCatalogId || !sizeableCatalogIds.has(sizingCatalogId)) return;
          const selectedCatalogId = sizingMap.get(sizingCatalogId);
          if (!selectedCatalogId || selections.has(selectedCatalogId)) return;
          selections.set(selectedCatalogId, item.lin ?? '');
        });
    });

    if (selections.size === 0) {
      setSizeAvailabilityByCatalogId((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    let cancelled = false;

    const loadAvailability = async () => {
      const catalogIds = Array.from(selections.keys());
      const availabilityLimit = Math.min(catalogIds.length * 400, 10_000);
      const assets = await iemService.getAvailableAssetsByCatalogIds({
        catalogIds,
        // Allow up to 400 assets per catalog size variant; cap at 10 000 total.
        limit: availabilityLimit,
      });

      if (cancelled) return;

      if (assets.length >= availabilityLimit) {
        console.warn(
          `[KitAssignmentDialog] availability fetch hit the limit (${availabilityLimit}); ` +
            'counts for some catalog IDs may be under-reported.',
        );
      }

      const countByCatalogId = new Map<string, number>();
      assets.forEach((asset: Entity) => {
        const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
        const rawCatalogId =
          typeof entityData.catalog_id === 'string' ? entityData.catalog_id.trim() : '';
        if (rawCatalogId && selections.has(rawCatalogId)) {
          countByCatalogId.set(rawCatalogId, (countByCatalogId.get(rawCatalogId) ?? 0) + 1);
        }
      });
      // Ensure all requested catalog IDs appear in the map (0 if none available)
      catalogIds.forEach((id) => {
        if (!countByCatalogId.has(id)) countByCatalogId.set(id, 0);
      });

      setSizeAvailabilityByCatalogId((prev) => {
        let changed = false;
        const next = new Map(prev);
        countByCatalogId.forEach((count, catalogId) => {
          if (next.get(catalogId) !== count) {
            next.set(catalogId, count);
            changed = true;
          }
        });
        if (!changed && next.size === prev.size) {
          return prev;
        }
        return next;
      });
    };

    void loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    kitId,
    ieSummaryByPerson,
    draftIds,
    existingAssignmentsSet,
    sizingByPersonAndParent,
    kitItemCatalogIdById,
    sizeableCatalogIds,
    syntheticIeItemsForKit,
  ]);

  const computeKitCounts = useMemo(() => {
    return (
      items: Array<{ ieKitId?: string | null; currentQty: number; authorizedQty: number }>,
    ) => {
      return items.reduce(
        (acc, item) => {
          if (item.ieKitId === kitId) {
            acc.current += item.currentQty ?? 0;
            acc.authorized += item.authorizedQty ?? 0;
          }
          return acc;
        },
        { current: 0, authorized: 0 },
      );
    };
  }, [kitId]);

  const createReserveKeyResolver = useMemo(() => {
    const normalizeKey = (value?: string | null) => (value ?? '').trim().toLowerCase();

    return (items: IEItem[]) => {
      const catalogIdToPreferredKeys = new Map<string, Set<string>>();

      items.forEach((item) => {
        const normalizedCatalogId = normalizeKey(item.catalogId);
        if (normalizedCatalogId) {
          if (!catalogIdToPreferredKeys.has(normalizedCatalogId))
            catalogIdToPreferredKeys.set(normalizedCatalogId, new Set());
          catalogIdToPreferredKeys.get(normalizedCatalogId)!.add(`catalog:${normalizedCatalogId}`);
        }
      });

      const resolveForItem = (item: IEItem) => {
        const normalizedCatalogId = normalizeKey(item.catalogId);
        if (normalizedCatalogId) return `catalog:${normalizedCatalogId}`;
        return 'unknown';
      };

      const resolveForCatalogId = (catalogId: string) => {
        const normalizedCatalogId = normalizeKey(catalogId);
        if (!normalizedCatalogId) return 'unknown';
        return `catalog:${normalizedCatalogId}`;
      };

      return { resolveForItem, resolveForCatalogId };
    };
  }, []);

  const refreshReservedCounts = useMemo(() => {
    return async (personId: string) => {
      if (!kitId) return null;
      const ieSummary = await iemService.getIESummary(personId);
      const counts = computeKitCounts(ieSummary);
      setReservedCounts((prev) => {
        const next = new Map(prev);
        next.set(personId, counts);
        return next;
      });
      setIeSummaryByPerson((prev) => {
        const next = new Map(prev);
        next.set(personId, ieSummary);
        return next;
      });
      return ieSummary;
    };
  }, [computeKitCounts, kitId]);

  // --- State Sync ---

  useEffect(() => {
    if (!open) {
      setLoadedKitId(null);
      setSizingPerson(null);
      setHideExisting(false);
      setConfirmDiscardOpen(false);
      setLoadingReservations(new Set());
      setReservedCounts(new Map());
      setIeSummaryByPerson(new Map());
      setReservedCartOverrides(new Set());
      setSessionReservedByPerson(new Map());
      return;
    }
    setAvailableSearch('');
    setAssignedSearch('');
    setErrorMessage(null);
    setIsSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || !kitId) {
      setReservedCounts(new Map());
      setIeSummaryByPerson(new Map());
      setReservedCartOverrides(new Set());
      setSessionReservedByPerson(new Map());
      return;
    }
    setReservedCounts(new Map());
    setIeSummaryByPerson(new Map());
    setReservedCartOverrides(new Set());
    setSessionReservedByPerson(new Map());
  }, [open, kitId]);

  useEffect(() => {
    if (!open || !kitId || draftIds.size === 0) return;

    const pendingIds = Array.from(draftIds).filter((id) => !reservedCounts.has(id));
    if (pendingIds.length === 0) return;

    let cancelled = false;

    const loadCounts = async () => {
      const results = await Promise.all(
        pendingIds.map(async (personId) => ({
          personId,
          ieSummary: await iemService.getIESummary(personId),
        })),
      );

      if (cancelled) return;

      setReservedCounts((prev) => {
        const next = new Map(prev);
        results.forEach(({ personId, ieSummary }) => {
          next.set(personId, computeKitCounts(ieSummary));
        });
        return next;
      });
      setIeSummaryByPerson((prev) => {
        const next = new Map(prev);
        results.forEach(({ personId, ieSummary }) => {
          next.set(personId, ieSummary);
        });
        return next;
      });
    };

    void loadCounts();

    return () => {
      cancelled = true;
    };
  }, [open, kitId, draftIds, reservedCounts, computeKitCounts]);

  const buildReserveStockPlanForPerson = useMemo(() => {
    return (personId: string, ieSummary: IEItem[]) => {
      if (!kitId) {
        return { allocations: [], placeholders: [], missingDueToStock: 0 };
      }

      const personSizingMap = sizingByPersonAndParent.get(personId);
      const sessionReservedMap = sessionReservedByPerson.get(personId);
      const dbReservedMap = dbReservedByPerson.get(personId);
      const reserveKeyResolver = createReserveKeyResolver(ieSummary);

      const items = ieSummary
        .filter((item) => item.ieKitId === kitId)
        .map((item) => {
          const catalogId = item.catalogId ?? undefined;
          const kitCatalogId = item.kitItemId ? kitItemCatalogIdById.get(item.kitItemId) : null;
          const sizingCatalogId = kitCatalogId ?? catalogId ?? null;
          const requiresSize = sizingCatalogId ? sizeableCatalogIds.has(sizingCatalogId) : false;
          const selectedCatalogId =
            requiresSize && sizingCatalogId ? personSizingMap?.get(sizingCatalogId) : null;

          const reserveKey = reserveKeyResolver.resolveForItem(item);
          const reservedAlready =
            (sessionReservedMap?.get(reserveKey) ?? 0) + (dbReservedMap?.get(reserveKey) ?? 0);
          const need = Math.max(item.authorizedQty - item.currentQty - reservedAlready, 0);

          const maxAllocatableQty =
            typeof item.availableQty === 'number' ? Math.max(item.availableQty, 0) : undefined;

          const selectedAvailability = selectedCatalogId
            ? sizeAvailabilityByCatalogId.get(selectedCatalogId)
            : undefined;
          const effectiveAvailability =
            selectedAvailability ??
            (typeof maxAllocatableQty === 'number' ? maxAllocatableQty : undefined);
          const isOutOfStock =
            typeof effectiveAvailability === 'number' && !Number.isNaN(effectiveAvailability)
              ? effectiveAvailability <= 0
              : false;

          return {
            id: `${personId}-${item.catalogId ?? item.lin ?? item.item}`,
            itemName: item.item,
            lin: item.lin,
            catalogId: catalogId ?? undefined,
            ieKitId: kitId,
            need,
            existingPlaceholderQty: 0,
            requiresSize,
            selectedCatalogId,
            maxAllocatableQty: isOutOfStock ? 0 : effectiveAvailability,
            placeholderCatalogId: requiresSize ? (sizingCatalogId ?? undefined) : undefined,
          };
        });

      return buildReserveStockPlan(items);
    };
  }, [
    kitId,
    createReserveKeyResolver,
    sessionReservedByPerson,
    dbReservedByPerson,
    sizingByPersonAndParent,
    sizeAvailabilityByCatalogId,
    sizeableCatalogIds,
    kitItemCatalogIdById,
  ]);

  useEffect(() => {
    if (open && kitId && !assignmentsLoading && loadedKitId !== kitId) {
      const ids = new Set<string>();
      assignments.forEach((a) => {
        const pid = (a.entity_data as AssignmentEntityData)?.person_id;
        if (pid) ids.add(pid);
      });
      setDraftIds(ids);
      setLoadedKitId(kitId);
    }
  }, [open, kitId, assignments, assignmentsLoading, loadedKitId]);

  // --- Computed Views ---

  const peopleLookup = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const peopleSearchText = useMemo(
    () => new Map(people.map((p) => [p.id, normalizePersonText(p)])),
    [people],
  );

  const leftItems: SelectionItem[] = useMemo(() => {
    const needle = availableSearch.trim().toLowerCase();
    return people
      .filter((p) => !draftIds.has(p.id))
      .filter((p) => !needle || peopleSearchText.get(p.id)?.includes(needle))
      .slice(0, 50)
      .map((p) => ({
        id: p.id,
        primaryText: p.display_name,
        secondaryText: getPersonSubtitle(p, individualIdPrefix),
        disabled: atLimit,
      }));
  }, [people, draftIds, availableSearch, peopleSearchText, individualIdPrefix, atLimit]);

  const totalSizeable = sizeableCatalogIds.size;

  const rightItems: SelectionItem[] = useMemo(() => {
    const needle = assignedSearch.trim().toLowerCase();
    return Array.from(draftIds)
      .filter((id) => !hideExisting || !existingAssignmentsSet.has(id))
      .map((id) => {
        const person = peopleLookup.get(id);
        const sizedCount = personSizedCounts.get(id) ?? 0;
        return {
          id,
          person,
          text: peopleSearchText.get(id) ?? '',
          sizedCount,
        };
      })
      .filter((item) => !needle || item.text.includes(needle))
      .sort((a, b) => (a.person?.display_name ?? '').localeCompare(b.person?.display_name ?? ''))
      .map((item) => ({
        id: item.id,
        primaryText: item.person?.display_name ?? 'Unknown Person',
        secondaryText: item.person
          ? getPersonSubtitle(item.person, individualIdPrefix)
          : 'ID: ' + item.id,
        data: {
          person: item.person,
          sizedCount: item.sizedCount,
        },
      }));
  }, [
    draftIds,
    assignedSearch,
    peopleLookup,
    peopleSearchText,
    personSizedCounts,
    hideExisting,
    existingAssignmentsSet,
    individualIdPrefix,
  ]);

  const hiddenAssignedCount = useMemo(() => {
    if (!hideExisting) return 0;
    return Array.from(draftIds).filter((id) => existingAssignmentsSet.has(id)).length;
  }, [hideExisting, draftIds, existingAssignmentsSet]);

  const rightTitle =
    hiddenAssignedCount > 0 ? `Assigned [${hiddenAssignedCount} hidden]` : 'Assigned';

  // --- Handlers ---

  const handleAdd = (item: SelectionItem) => {
    if (!existingAssignmentsSet.has(item.id) && atLimit) {
      setErrorMessage(
        `Maximum of ${BULK_SAVE_LIMIT} people can be added per assignment session. Remove someone to add another.`,
      );
      return;
    }
    setDraftIds((prev) => new Set(prev).add(item.id));
  };
  const handleRemove = (item: SelectionItem) => setPendingRemoveItem(item);

  const handleConfirmRemove = () => {
    if (!pendingRemoveItem) return;
    setDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(pendingRemoveItem.id);
      return next;
    });
    setPendingRemoveItem(null);
  };

  const handleSave = async () => {
    if (!kitId || !kit) return;
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const currentAssignmentsMap = new Map(
        assignments.map((a) => [(a.entity_data as AssignmentEntityData).person_id, a]),
      );

      const toCreate = Array.from(draftIds).filter((id) => !currentAssignmentsMap.has(id));
      const toDelete = Array.from(currentAssignmentsMap.keys()).filter(
        (id): id is string => id !== undefined && !draftIds.has(id),
      );

      if (toCreate.length === 0 && toDelete.length === 0) {
        onClose();
        return;
      }

      if (toCreate.length) {
        await entityService.createBulkEntities(
          toCreate.map((pid) => {
            const person = peopleLookup.get(pid)!;
            return {
              entity_type: 'person_ie_kit',
              display_name: `${person.display_name} • ${kit.display_name}`,
              entity_identifier: buildAssignmentIdentifier(person, kit),
              entity_data: { person_id: pid, ie_kit_id: kitId },
            };
          }),
          actorId,
        );
      }

      if (toDelete.length) {
        await Promise.all(
          toDelete.map((pid) => entityService.remove({ id: currentAssignmentsMap.get(pid)!.id })),
        );
      }

      // Cascade ie_kit_id updates to hand receipts for changed assignments.
      if (kitId && (toCreate.length > 0 || toDelete.length > 0)) {
        try {
          await iemService.updatePersonIeKitAssignment({
            ieKitId: kitId,
            addedPersonIds: toCreate,
            removedPersonIds: toDelete,
          });
        } catch (cascadeErr) {
          // Log but don't block the save — the frontend fallback handles stale ie_kit_id.
          console.warn('Hand receipt ie_kit_id cascade failed:', cascadeErr);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      notifications.success('Assignments updated', `${kit.display_name} updated.`);
      onSaved?.({ added: toCreate.length, removed: toDelete.length });
      onClose();
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      const hint =
        typeof err === 'object' && err !== null && 'details' in err
          ? (err as { details?: unknown }).details
          : undefined;
      const errorMessage =
        typeof hint === 'string' && hint.trim().length > 0 ? `${message} — ${hint}` : message;
      setErrorMessage(errorMessage);
      notifications.error('Save failed', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReserveStock = async (personId: string) => {
    if (!kitId) return;

    const activeCart = activeIssueCartByPersonId.get(personId);
    if (activeCart || reservedCartOverrides.has(personId)) {
      notifications.warning('Cart exists', 'Reserve cart already exists for this member.');
      return;
    }

    if (reservationInFlightRef.current.has(personId)) return;
    reservationInFlightRef.current.add(personId);

    setLoadingReservations((prev) => {
      const next = new Set(prev);
      next.add(personId);
      return next;
    });

    try {
      // Ensure sizing data is fresh, especially if user just updated it in the dialog
      await queryClient.invalidateQueries({
        queryKey: ['entities', 'list', 'person_sizing'],
      });

      // Check if this is a new (unsaved) assignment
      const isNewAssignment = !existingAssignmentsSet.has(personId);

      let ieSummary: IEItem[] | null = null;
      try {
        ieSummary = await refreshReservedCounts(personId);
      } catch (err) {
        if (!isNewAssignment) {
          throw err;
        }
      }
      if (!ieSummary && !isNewAssignment) return;

      // For new assignments, use synthetic IE Kit items; for existing, use actual IE Kit summary
      const effectiveIeItems = isNewAssignment ? syntheticIeItemsForKit : ieSummary;
      if (!effectiveIeItems || effectiveIeItems.length === 0) return;

      const reservePlan = buildReserveStockPlanForPerson(personId, effectiveIeItems);
      const itemsToAllocate = reservePlan.allocations;
      const unsizedPlaceholders = reservePlan.placeholders;
      const missingDueToStock = reservePlan.missingDueToStock;
      const reserveKeyResolver = createReserveKeyResolver(effectiveIeItems);

      let allocatedCount = 0;
      let placeholderCount = 0;

      // Allocate sized items
      if (itemsToAllocate.length > 0) {
        try {
          const result = await bulkAllocateMutation.mutateAsync({
            customerId: personId,
            items: itemsToAllocate,
          });

          if (result.success) {
            allocatedCount = result.allocated_count ?? 0;
            lastReservationByPersonRef.current.set(personId, Date.now());

            if (result.items && result.items.length > 0) {
              setSessionReservedByPerson((prev) => {
                const next = new Map(prev);
                const personMap = new Map(next.get(personId) ?? []);
                result.items?.forEach((item) => {
                  if (item.allocated <= 0) return;
                  const key = reserveKeyResolver.resolveForCatalogId(item.catalog_id);
                  personMap.set(key, (personMap.get(key) ?? 0) + item.allocated);
                });
                next.set(personId, personMap);
                return next;
              });

              // Decrement in-memory availability so subsequent people in the same
              // batch don't over-allocate against now-reserved stock.
              setSizeAvailabilityByCatalogId((prev) => {
                const next = new Map(prev);
                result.items?.forEach((item) => {
                  if (item.allocated <= 0) return;
                  const current = next.get(item.catalog_id) ?? 0;
                  next.set(item.catalog_id, Math.max(current - item.allocated, 0));
                });
                return next;
              });
            }
          }
        } catch {
          // Continue to try placeholders even if allocation fails
        }
      }

      // Create placeholders for unsized items
      if (unsizedPlaceholders.length > 0) {
        try {
          const result = await cartService.bulkUpsertSizeAllocationCart({
            customerId: personId,
            items: unsizedPlaceholders.map((placeholder) => ({
              parentCatalogId: placeholder.parentCatalogId,
              quantity: placeholder.quantity,
            })),
          });
          if (result.success) {
            placeholderCount = unsizedPlaceholders.reduce(
              (sum, placeholder) => sum + placeholder.added,
              0,
            );
            void queryClient.invalidateQueries({ queryKey: cartQueryKeys.cart(personId) });
          }
        } catch {
          // Continue even if placeholders fail
        }
      }

      const summaryParts = [
        `Sized: ${allocatedCount}`,
        `Unsized: ${placeholderCount}`,
        `No stock: ${missingDueToStock}`,
      ];
      notifications.success('Reserve stock summary', summaryParts.join(' • '));

      if (allocatedCount > 0 || placeholderCount > 0) {
        setReservedCartOverrides((prev) => {
          const next = new Set(prev);
          next.add(personId);
          return next;
        });
        void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      }

      await refreshReservedCounts(personId).catch(() => undefined);
      void queryClient.invalidateQueries({
        queryKey: ['entities', 'list', 'asset_reservation'],
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unable to reserve stock.';
      notifications.error('Failed to reserve stock', message);
    } finally {
      reservationInFlightRef.current.delete(personId);
      setLoadingReservations((prev) => {
        const next = new Set(prev);
        next.delete(personId);
        return next;
      });
    }
  };

  const renderRightExtras = (item: SelectionItem) => {
    const person = item.data?.person as Entity | undefined;
    const sizedCount = (item.data?.sizedCount as number) ?? 0;
    const isComplete = sizedCount >= totalSizeable;
    const color = isComplete ? 'success.main' : 'warning.main';
    const ieSummary = ieSummaryByPerson.get(item.id);
    const hasSummary = ieSummaryByPerson.has(item.id);

    // Check if this is a newly added (unsaved) assignment
    const isNewAssignment = !existingAssignmentsSet.has(item.id);

    // For new assignments, use synthetic IE Kit items; for existing, use actual IE Kit summary
    const effectiveIeItems = isNewAssignment ? syntheticIeItemsForKit : ieSummary;
    const isSummaryLoading = isNewAssignment
      ? syntheticIeItemsForKit.length === 0 && kitItems.length > 0
      : draftIds.has(item.id) && !hasSummary;

    const hasAssignedItems =
      ieSummary?.some((summaryItem) => {
        if (!kitId || summaryItem.ieKitId !== kitId) return false;
        return (summaryItem.currentQty ?? 0) > 0;
      }) ?? false;
    const reservePlan = effectiveIeItems
      ? buildReserveStockPlanForPerson(item.id, effectiveIeItems)
      : null;
    const hasReservableItems =
      !!reservePlan && (reservePlan.allocations.length > 0 || reservePlan.placeholders.length > 0);
    const activeCart = activeIssueCartByPersonId.get(item.id);
    const hasExistingCart = reservedCartOverrides.has(item.id) || Boolean(activeCart);
    const isReserving = loadingReservations.has(item.id);
    const reserveBlocked =
      hasExistingCart || hasAssignedItems || isSummaryLoading || !hasReservableItems;
    const reserveTooltip = isReserving
      ? 'Reserving stock...'
      : reserveBlocked
        ? hasExistingCart
          ? 'Reserve cart already exists for this member.'
          : hasAssignedItems
            ? "Can't reserve stock: some kit items are already assigned."
            : isSummaryLoading
              ? 'Loading assignment data...'
              : 'No reservable items for this kit.'
        : 'Reserve all stock for this kit.';

    return (
      <Stack direction="row" alignItems="center" spacing={1} mr={1}>
        <Tooltip title={reserveTooltip}>
          <span>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => person && handleReserveStock(person.id)}
              disabled={!person || !kitId || isReserving || reserveBlocked}
              startIcon={isReserving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {isReserving ? 'Reserving...' : 'Reserve Stock'}
            </Button>
          </span>
        </Tooltip>
        {totalSizeable > 0 && (
          <Typography variant="caption" color={color} fontWeight="bold">
            {sizedCount} / {totalSizeable} Sized
          </Typography>
        )}
        <Tooltip title="Manage Sizes">
          <IconButton
            size="small"
            aria-label="Manage Sizes"
            onClick={() => person && setSizingPerson(person)}
            color={isComplete ? 'default' : 'warning'}
          >
            <RulerIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  const headerContent = (
    <Stack direction="column" gap={1}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
        <Typography variant="h5">Target {ieKitLabel}:</Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 'normal' }}>
          {kit?.display_name ?? 'Loading...'}
          <Typography component="span" variant="body2" sx={{ ml: 2, color: 'text.disabled' }}>
            {formatIeKitId(kit?.entity_identifier)}
          </Typography>
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideExisting}
              onChange={(e) => setHideExisting(e.target.checked)}
              size="small"
            />
          }
          label="Hide Existing Assignments"
        />
      </Stack>
      {atLimit && (
        <Alert severity="warning">
          Maximum of {BULK_SAVE_LIMIT} new people per session reached. Save the current batch or
          remove someone to add another.
        </Alert>
      )}
    </Stack>
  );

  const missingKitWarning = !kit;

  return (
    <>
      {missingKitWarning && open && <Alert severity="warning">{ieKitLabel} data missing.</Alert>}
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        aria-labelledby="discard-assignment-changes-dialog-title"
      >
        <DialogTitle id="discard-assignment-changes-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved assignment changes. Are you sure you want to close?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>Keep Editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onClose();
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      <DualPaneSelectionDialog
        open={open}
        title={`${ieKitLabel} Assignments`}
        onClose={handleRequestClose}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel="Save Assignments"
        saveDisabled={isViewer || missingKitWarning || newAssignmentCount > BULK_SAVE_LIMIT}
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage(null)}
        headerContent={headerContent}
        leftTitle="Available People"
        leftItems={leftItems}
        leftSearch={availableSearch}
        onLeftSearchChange={setAvailableSearch}
        onLeftItemAdd={handleAdd}
        leftLoading={peopleLoading}
        leftError={!!peopleError}
        rightTitle={rightTitle}
        rightCount={draftIds.size}
        rightItems={rightItems}
        rightSearch={assignedSearch}
        onRightSearchChange={setAssignedSearch}
        onRightItemRemove={handleRemove}
        rightLoading={assignmentsLoading}
        rightError={!!assignmentsError}
        renderRightItemExtras={renderRightExtras}
        readOnly={isViewer}
      />

      <PersonSizingDialog
        open={!!sizingPerson}
        onClose={() => setSizingPerson(null)}
        person={sizingPerson ?? undefined}
      />

      <Dialog open={pendingRemoveItem !== null} onClose={() => setPendingRemoveItem(null)}>
        <DialogTitle>Remove Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>
              {typeof pendingRemoveItem?.primaryText === 'string'
                ? pendingRemoveItem.primaryText
                : 'this person'}
            </strong>{' '}
            from this {ieKitLabel.toLowerCase()} assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoveItem(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
